import { Info, WifiOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useBanner } from './banner';
import { anunciar, type NovoRetorno } from './retorno';

/**
 * As duas condições do SISTEMA que viram banner (p.33), as mesmas em toda
 * tela: há uma versão nova do pacote no servidor, e a rede caiu. Quem as vigia
 * é a casca, que monta o banner; as condições de negócio (máquina sem
 * apontamento) são registradas pela tela que as conhece.
 */

/* ------------------------------------------------------ nova versão */

/** De quanto em quanto tempo a casca pergunta ao servidor pelo pacote. */
export const INTERVALO_DA_VERSAO_MS = 5 * 60_000;

/**
 * A ASSINATURA DO PACOTE: os scripts de entrada com hash (`/assets/…`) que o
 * `index.html` pede. Uma implantação nova troca o nome deles; o servidor que
 * serve outro nome serve outro sistema.
 *
 * Por que não o `versao` de `GET /saude`: ele é o commit da IMAGEM DO
 * BACKEND, e não diz nada sobre o pacote carregado nesta aba — uma subida só
 * do backend pediria para recarregar sem mudar uma linha da tela, e a tela
 * não sabe de que commit ela mesma veio. O nome com hash é a própria tela.
 *
 * Em desenvolvimento o `index.html` pede `/src/main.tsx`, sem hash: a
 * assinatura sai vazia e a vigia não liga (o Vite já recarrega sozinho).
 */
export function assinaturaDoHtml(html: string): string {
  const encontrados: string[] = [];
  for (const m of html.matchAll(/<script\b[^>]*\bsrc="([^"]+)"[^>]*>/gi)) {
    const src = m[1] ?? '';
    const caminho = src.replace(/^https?:\/\/[^/]+/i, '');
    if (/\/assets\//.test(caminho)) encontrados.push(caminho);
  }
  return encontrados.sort().join(' ');
}

/** A assinatura do pacote que ESTA aba carregou. */
function assinaturaDaPagina(): string {
  const encontrados: string[] = [];
  for (const s of document.querySelectorAll<HTMLScriptElement>('script[src]')) {
    const caminho = new URL(s.src, location.href).pathname;
    if (/\/assets\//.test(caminho)) encontrados.push(caminho);
  }
  return encontrados.sort().join(' ');
}

/**
 * Pergunta ao servidor, a cada 5 minutos e sempre que a aba volta ao foco, se
 * o `index.html` ainda pede o mesmo pacote. Quando não, registra o banner
 * informativo "Recarregar" — e para de perguntar: a condição só acaba ao
 * recarregar.
 *
 * `caminhoDoIndex`: `/index.html` na mesa, `/m/index.html` no celular. O
 * service worker do celular não guarda esta leitura (não é navegação nem
 * `/assets/`), e `no-store` passa por cima do cache do navegador.
 */
export function useBannerDeVersaoNova(caminhoDoIndex: string): void {
  const [nova, setNova] = useState<string | null>(null);

  useEffect(() => {
    const daPagina = assinaturaDaPagina();
    if (daPagina === '') return;
    let vivo = true;
    let achou = false;
    const conferir = async () => {
      if (achou || document.visibilityState === 'hidden') return;
      try {
        const resposta = await fetch(`${caminhoDoIndex}?versao=${String(Date.now())}`, { cache: 'no-store', credentials: 'same-origin' });
        if (!resposta.ok) return;
        const doServidor = assinaturaDoHtml(await resposta.text());
        if (vivo && doServidor !== '' && doServidor !== daPagina) {
          achou = true;
          setNova(doServidor);
        }
      } catch {
        /* Sem rede ou servidor fora: não é versão nova. Tenta na próxima. */
      }
    };
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') void conferir();
    };
    const relogio = setInterval(() => void conferir(), INTERVALO_DA_VERSAO_MS);
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);
    return () => {
      vivo = false;
      clearInterval(relogio);
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
    };
  }, [caminhoDoIndex]);

  useBanner(
    nova === null
      ? null
      : {
          /* O id leva o pacote novo: dispensar esta versão não cala a próxima. */
          id: `versao:${nova}`,
          gravidade: 'informativo',
          icone: Info,
          texto: 'Há uma nova versão do sistema. Recarregue para atualizar.',
          acao: {
            rotulo: 'Recarregar',
            aoAcionar: () => {
              location.reload();
            },
          },
          rotuloDeFechar: 'Fechar o aviso de nova versão',
        },
  );
}

/* -------------------------------------------------------- sem conexão */

/**
 * O aparelho diz que está sem rede: banner CRÍTICO, que não fecha enquanto
 * durar (p.33), com a hora em que caiu — "o tamanho da condição, com número".
 *
 * Só quando o navegador AFIRMA estar sem rede (`navigator.onLine === false`):
 * onde a propriedade não existe, não se afirma nada.
 *
 * O texto da mesa não é o do exemplo da p.33 ("as alterações ficam guardadas
 * e são enviadas quando a rede voltar"): a mesa não tem fila de envio — só o
 * app do celular tem (M30) —, e prometer o envio seria mentir. Ela diz o que
 * acontece e o que fazer.
 *
 * Quando a rede volta (R06, p.67), o banner some sozinho e entra o retorno
 * "Conexão de volta". O da p.67 diz "· 3 lançamentos enviados" porque o
 * celular tem fila; a mesa não guardou nada, e diz isso: quanto tempo ficou
 * sem rede e que já pode salvar.
 */
export function useBannerSemConexao(): void {
  const [caiuEm, setCaiuEm] = useState<Date | null>(() =>
    typeof navigator !== 'undefined' && navigator.onLine === false ? new Date() : null,
  );

  /* A hora da queda, fora do estado, para o retorno sair do evento (e não de
     dentro de um atualizador, que o React pode chamar duas vezes). */
  const desde = useRef<Date | null>(caiuEm);

  useEffect(() => {
    const caiu = () => {
      desde.current ??= new Date();
      setCaiuEm(desde.current);
    };
    const voltou = () => {
      if (desde.current !== null) anunciar(retornoDaConexaoDeVolta(desde.current));
      desde.current = null;
      setCaiuEm(null);
    };
    window.addEventListener('offline', caiu);
    window.addEventListener('online', voltou);
    return () => {
      window.removeEventListener('offline', caiu);
      window.removeEventListener('online', voltou);
    };
  }, []);

  useBanner(
    caiuEm === null
      ? null
      : {
          id: 'sem-conexao',
          gravidade: 'critico',
          icone: WifiOff,
          texto: textoSemConexao(caiuEm),
        },
  );
}

/**
 * O retorno de quando a rede volta, na mesa (R06, p.67). Sem fila, sem
 * contagem de enviados: o que houve (desde quando) e o que fazer.
 */
export function retornoDaConexaoDeVolta(caiuEm: Date): NovoRetorno {
  const hora = caiuEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return {
    tom: 'sucesso',
    titulo: 'Conexão de volta',
    fatos: [`Sem rede desde ${hora}`, 'Você já pode salvar'],
    nota: 'Nada ficou guardado enquanto a rede esteve fora: a mesa não tem fila de envio.',
  };
}

/** "Sem conexão desde 14:32. Nada é gravado até a rede voltar; espere o sinal para salvar." */
export function textoSemConexao(caiuEm: Date): string {
  const hora = caiuEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `Sem conexão desde ${hora}. Nada é gravado até a rede voltar; espere o sinal para salvar.`;
}
